import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const FlightEmission = () => {
  // State variables for flight emission calculation
  const [distance, setDistance] = useState(0); // Distance of the flight
  const [emissionFactor, setEmissionFactor] = useState(0); // Emission factor in kg CO2 per km
  const [emissions, setEmissions] = useState(0); // Calculated emissions in kg CO2

  // Event handlers for input changes
  const handleDistanceChange = (e) =>
    setDistance(parseFloat(e.target.value) || 0);
  const handleEmissionFactorChange = (e) =>
    setEmissionFactor(parseFloat(e.target.value) || 0);

  // useEffect to calculate emissions
  useEffect(() => {
    if (distance > 0 && emissionFactor > 0) {
      const emissionsResult = distance * emissionFactor; // Calculating emissions
      setEmissions(emissionsResult);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [distance, emissionFactor]);

  return (
    <Layout title="Flight Emission Calculator" path="flight-emission">
      <section className="section-padding" id="flight-emission">
        <div className="container mt-5">
          <div className="row">
            {/* Flight Emission Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Flight Emission Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Distance */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="distance" className="form-label">
                        Distance (km)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Distance:</strong> The flight distance in kilometers between your departure and arrival locations."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="distance"
                        placeholder="Enter flight distance in kilometers"
                        value={distance || ""}
                        onChange={handleDistanceChange}
                      />
                    </div>

                    {/* Input for Emission Factor */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="emissionFactor" className="form-label">
                        Emission Factor (kg CO2 per km)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Emission Factor:</strong> The amount of carbon dioxide (CO2) emitted per kilometer of flight, typically given in kg CO2/km."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="emissionFactor"
                        placeholder="Enter emission factor in kg CO2 per km"
                        value={emissionFactor || ""}
                        onChange={handleEmissionFactorChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Emissions:{" "}
                        <span className="fw-bold ms-3">
                          {emissions === 0
                            ? "0"
                            : emissions.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          kg CO2
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Flight Emission Calculation
                </h3>

                {/* What is Flight Emission? */}
                <h4 className="mt-3 mb-3">What is Flight Emission?</h4>
                <p>
                  **Flight Emission** refers to the carbon dioxide (CO2)
                  emissions produced by an aircraft during a flight. It depends
                  on the flight distance and the emission factor, which
                  represents the amount of CO2 emitted per kilometer flown.
                </p>

                {/* Why Use the Flight Emission Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use the Flight Emission Calculator?
                </h4>
                <p>
                  This calculator helps travelers and organizations assess the
                  environmental impact of air travel and encourages more
                  eco-friendly travel decisions.
                </p>

                {/* Benefits of Using the Flight Emission Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Flight Emission Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Supports Eco-Friendly Travel:</strong> Helps
                    identify flight emissions and take action toward reducing
                    carbon footprints.
                  </li>
                  <li>
                    <strong>Improves Sustainability Efforts:</strong> Provides a
                    tangible way for individuals and organizations to track
                    emissions.
                  </li>
                  <li>
                    <strong>Informed Travel Decisions:</strong> Allows travelers
                    to make informed decisions about flight routes and
                    alternatives.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Flight Emission Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Eco-Conscious Travelers:</strong> Individuals who
                    want to reduce their carbon footprint.
                  </li>
                  <li>
                    <strong>Travel Agencies:</strong> To calculate and
                    communicate the environmental impact of travel packages.
                  </li>
                  <li>
                    <strong>Environmental Organizations:</strong> For assessing
                    and promoting sustainable travel practices.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h5 className="mt-3 mb-2">
                  How can I offset my flight emissions?
                </h5>
                <p>
                  You can offset your flight emissions by investing in carbon
                  offset projects, such as reforestation or renewable energy
                  initiatives.
                </p>

                <h5 className="mt-3 mb-2">What is an Emission Factor?</h5>
                <p>
                  The emission factor is the amount of CO2 emitted per unit of
                  distance (e.g., per kilometer) traveled by an aircraft. It
                  varies by aircraft type, fuel efficiency, and other factors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FlightEmission;
